//Landlord comment section
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Avatar,
    Chip,
    CircularProgress,
    FormControlLabel,
    Switch,
    Divider
} from '@mui/material';
import { MessageCircle, Send } from 'react-feather';

const CommentsSection = ({ ticket, onAddComment }) => {
    const [newComment, setNewComment] = useState('');
    const [internalComment, setInternalComment] = useState(false);
    const [newCommentLoading, setNewCommentLoading] = useState(false);
    const [showInternalComments, setShowInternalComments] = useState(true);

    // Handle new comment
    const handleNewCommentChange = (event) => {
        setNewComment(event.target.value);
    };

    // Toggle internal comment
    const toggleInternalComment = () => {
        setInternalComment(!internalComment);
    };

    // Toggle showing internal comments
    const toggleShowInternalComments = () => {
        setShowInternalComments(!showInternalComments);
    };

    // Submit comment
    const submitComment = async () => {
        if (!newComment.trim()) return;

        try {
            setNewCommentLoading(true);
            const success = await onAddComment(newComment, internalComment);

            if (success) {
                setNewComment('');
            }
        } catch (error) {
            console.error("Error submitting comment:", error);
        } finally {
            setNewCommentLoading(false);
        }
    };

    // Filter visible comments based on internal flag
    const getVisibleComments = (comments) => {
        if (!comments) return [];
        if (showInternalComments) return comments;
        return comments.filter(comment => !comment.is_internal && !comment.isInternal);
    };

    // Get initials for avatar
    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase();
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            console.error("Date formatting error:", error);
            return 'Invalid date';
        }
    };



    console.log("========================================================================================")
    console.log(ticket)
    console.log("========================================================================================")


    return (
        <>
            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MessageCircle size={16} />
                    Comments ({ticket.comments ? ticket.comments.length : 0})
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showInternalComments}
                            onChange={toggleShowInternalComments}
                            size="small"
                        />
                    }
                    label={<Typography variant="body2">Show Internal Comments</Typography>}
                />
            </Box>

            {ticket.comments && getVisibleComments(ticket.comments).length > 0 ? (
                <Box sx={{ mb: 3 }}>
                    {getVisibleComments(ticket.comments).map((comment) => (
                        <Paper
                            key={comment.comment_id || comment.id}
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 2,
                                borderRadius: '8px',
                                backgroundColor: comment.is_internal || comment.isInternal ? '#FEF3F2' :
                                    comment.author === ticket.tenant?.name ? '#EFF6FF' : '#F9FAFB',
                                border: '1px solid',
                                borderColor: comment.is_internal || comment.isInternal ? '#FEE4E2' :
                                    comment.author === ticket.tenant?.name ? '#DBEAFE' : '#E5E7EB'
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            fontSize: '0.75rem',
                                            mr: 1,
                                            bgcolor: comment.author === ticket.tenant?.name ? '#3B82F6' :
                                                comment.is_internal || comment.isInternal ? '#EF4444' : '#10B981'
                                        }}
                                    >
                                        {getInitials(comment.author || comment.author_name || '')}
                                    </Avatar>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {comment.author || comment.author_name || 'Unknown'}
                                        {(comment.authorRole || comment.author_type) && (
                                            <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                                ({comment.authorRole || comment.author_type})
                                            </Typography>
                                        )}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {(comment.is_internal || comment.isInternal) && (
                                        <Chip
                                            label="Internal"
                                            size="small"
                                            sx={{
                                                backgroundColor: '#FEE4E2',
                                                color: '#EF4444',
                                                height: '20px',
                                                fontSize: '0.6rem'
                                            }}
                                        />
                                    )}
                                    <Typography variant="caption" color="textSecondary">
                                        {formatDate(comment.timestamp || comment.date_created)}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography variant="body2">{comment.text || comment.comment}</Typography>
                        </Paper>
                    ))}
                </Box>
            ) : (
                <Box
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        mb: 3
                    }}
                >
                    <Typography variant="body2" color="textSecondary">
                        No comments yet
                    </Typography>
                </Box>
            )}

            {/* Add comment section */}
            <Box sx={{ mt: 2 }}>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your comment here..."
                    value={newComment}
                    onChange={handleNewCommentChange}
                    variant="outlined"
                    disabled={newCommentLoading}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px'
                        }
                    }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                        control={<Switch checked={internalComment} onChange={toggleInternalComment} color="error" />}
                        label={<Typography variant="body2">Mark as internal note (only visible to staff)</Typography>}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        endIcon={newCommentLoading ? <CircularProgress size={20} color="inherit" /> : <Send size={16} />}
                        disabled={!newComment.trim() || newCommentLoading}
                        onClick={submitComment}
                        sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            boxShadow: 'none'
                        }}
                    >
                        {internalComment ? 'Add Internal Note' : 'Add Comment'}
                    </Button>
                </Box>
            </Box>
        </>
    );
};

export default CommentsSection;